// Lab Templates list — index page for /lab/templates.
// Trim table backed by GET /api/lab/templates; click a row to edit.

import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { Panel } from '../components/Panel'
import { api } from '../api/client'

type LabTemplateRow = {
  id: number
  slug: string
  name: string
  purpose: string
  publication_id: number | null
  enabled: boolean
  has_selection_config: boolean
}

const PUB_SLUG_BY_ID: Record<number, string> = {
  1: 'rc_car_action',
  2: 'model_airplane_news',
  3: 'flight_journal',
  4: 'diecast',
}

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const PURPOSE_TONE: Record<string, string> = {
  renewal: 'text-amber-glow',
  welcome: 'text-radar-green',
  winback: 'text-radar-violet',
  marketing: 'text-radar-cyan',
  transactional: 'text-paper',
}

export function LabTemplatesPage() {
  const nav = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['lab', 'templates'],
    queryFn: () => api.get<LabTemplateRow[]>('/api/lab/templates'),
    staleTime: 30_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1300px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label">LAB · L4</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper mt-1">
            Lab Templates
          </h1>
          <p className="text-paper-trace text-[13px] mt-2 font-serif max-w-[820px]">
            Email templates with a <span className="font-mono text-paper">content_selection_config</span>{' '}
            that drives dynamic article tile selection. Editor configures
            both the HTML and the selection rules; preview lives on the
            Content Selector and the Campaign Dry-Run pages.
          </p>
        </div>
        <Button onClick={() => nav('/lab/templates/new')}>+ New Template</Button>
      </header>

      <Panel label="All Lab templates" accent="amber" bodyClassName="p-0">
        {isLoading && (
          <div className="label text-paper-trace py-8 text-center animate-pulse">LOADING</div>
        )}
        {!isLoading && data && data.length === 0 && (
          <div className="py-10 text-center">
            <div className="label text-paper-trace mb-3">No Lab templates yet</div>
            <Button onClick={() => nav('/lab/templates/new')}>Create the first one</Button>
          </div>
        )}
        {!isLoading && data && data.length > 0 && (
          <table className="w-full text-[13px]">
            <thead className="border-b border-ink-500 bg-ink-700/40">
              <tr className="text-left">
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Purpose</Th>
                <Th>Publication</Th>
                <Th>Selection config</Th>
                <Th>Enabled</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => {
                const pubSlug = t.publication_id != null
                  ? PUB_SLUG_BY_ID[t.publication_id]
                  : null
                const pubName = pubSlug ? PUB_NAMES[pubSlug] : 'Multi-title'
                return (
                  <tr key={t.id} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                    <Td>
                      <Link
                        to={`/lab/templates/${t.id}`}
                        className="font-serif text-paper hover:text-amber-glow"
                      >
                        {t.name}
                      </Link>
                    </Td>
                    <Td>
                      <span className="font-mono text-[11px] text-paper-trace">{t.slug}</span>
                    </Td>
                    <Td>
                      <span className={`font-mono text-[11px] uppercase tracking-label ${PURPOSE_TONE[t.purpose] ?? 'text-paper'}`}>
                        {t.purpose}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono text-[11px] text-paper-trace">{pubName}</span>
                    </Td>
                    <Td>
                      {t.has_selection_config ? (
                        <span className="font-mono text-[10px] text-radar-green border border-radar-green/40 px-1.5 py-0.5">
                          CONFIGURED
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] text-radar-red border border-radar-red/40 px-1.5 py-0.5">
                          MISSING
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span className={`font-mono text-[11px] ${t.enabled ? 'text-radar-green' : 'text-paper-trace'}`}>
                        {t.enabled ? 'on' : 'off'}
                      </span>
                    </Td>
                    <Td>
                      <Link
                        to={`/lab/templates/${t.id}`}
                        className="font-mono text-[11px] uppercase tracking-label text-amber-glow hover:underline"
                      >
                        edit →
                      </Link>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 label text-paper-trace font-normal">{children}</th>
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-2.5 align-middle">{children}</td>
}
